import React, { useState, useEffect, useRef, useCallback } from 'react';

export default function RestTimer({ seconds, onClose }) {
  const [remaining, setRemaining] = useState(seconds);
  const totalRef = useRef(seconds);
  const intervalRef = useRef(null);
  const [breathPhase, setBreathPhase] = useState('in');

  const tick = useCallback(() => {
    setRemaining(prev => {
      if (prev <= 1) {
        clearInterval(intervalRef.current);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        setTimeout(onClose, 500);
        return 0;
      }
      return prev - 1;
    });
  }, [onClose]);

  useEffect(() => {
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [tick]);

  useEffect(() => {
    const breathInterval = setInterval(() => {
      setBreathPhase(prev => prev === 'in' ? 'out' : 'in');
    }, 4000);
    return () => clearInterval(breathInterval);
  }, []);

  const addTime = (amt) => {
    setRemaining(prev => prev + amt);
    totalRef.current += amt;
  };

  const progress = totalRef.current > 0 ? ((totalRef.current - remaining) / totalRef.current) * 100 : 0;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center" style={{ backgroundColor: 'rgba(18,18,20,0.96)', backdropFilter: 'blur(24px)' }} data-testid="rest-timer-overlay">
      {/* Breathing guide */}
      <div className="mb-6">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-[4000ms] ease-in-out ${
          breathPhase === 'in' ? 'scale-100' : 'scale-75'
        }`} style={{ border: '2px solid var(--theme-border)', backgroundColor: breathPhase === 'in' ? 'var(--theme-glow)' : 'transparent' }}>
          <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">
            {breathPhase === 'in' ? 'Breathe in' : 'Breathe out'}
          </span>
        </div>
      </div>

      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-700 mb-4">REST</div>

      {/* Timer ring */}
      <div className="relative w-44 h-44 mb-6">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
          <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
          <circle cx="80" cy="80" r="70" fill="none" stroke="var(--theme-color)" strokeWidth="6"
            strokeDasharray={`${progress * 4.4} 440`} strokeLinecap="round"
            style={{ filter: 'drop-shadow(0 0 6px var(--theme-glow-strong))' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="rest-timer-display">
          <span className="font-mono text-6xl font-bold text-white tabular-nums">
            {mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : secs}
          </span>
          <span className="text-[10px] text-neutral-700 uppercase tracking-widest mt-1">seconds</span>
        </div>
      </div>

      {/* Calm message */}
      <div className="text-xs text-neutral-600 italic mb-8 text-center px-8">
        {remaining > 60 ? "Relax. Let your muscles recover." : remaining > 30 ? "Almost time. Visualize the next set." : "Get ready. Next set incoming."}
      </div>

      <div className="flex gap-3">
        <button onClick={() => addTime(30)}
          className="glass-elevated rounded-lg text-white font-head font-bold uppercase h-14 px-6 active:scale-95 transition-transform text-sm"
          data-testid="rest-timer-add-btn">
          +30s
        </button>
        <button onClick={onClose}
          className="bg-brand text-white font-head font-bold uppercase h-14 px-6 rounded-lg active:scale-95 transition-transform text-sm glow-subtle"
          data-testid="rest-timer-skip-btn">
          SKIP
        </button>
      </div>
    </div>
  );
}
